import { useMemo, useState } from 'react';
import type { DataTableRenderCellParams, DataTableRowEventHandler } from '@/components/custom/erp/data-table-v2';
import { ContextMenuBuilder, ContextMenu } from '@/components/custom/erp/input-table-v2';
import { ErpInputTableV2, useInputTableValidation } from '@/components/custom/erp';
import { constraintKey, constraintValues } from '@/lib/account-constraints';
import type { RepeatRowsPayload } from '@/components/custom/erp';
import { type SelectedCellInfo, useDetailPaste } from '../hooks';
import type { AccountConstraint } from '@/types/schemas';
import { copyCellValue } from '@/lib/table-handlers';
import { getDetailTableColumns } from './columns';
import { QUERY_KEYS } from '@/constants';
import { FormMode } from '@/types/form';
import { useCRUD } from '@/hooks';

interface DetailTabProps {
  formMode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: DataTableRowEventHandler;
  onCellClick?: (params: DataTableRenderCellParams) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: BanHangValue) => void;
  onRepeatForRows: (payload: RepeatRowsPayload<BanHangRecord>) => void;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<BanHangRecord[]>; resultField?: string }>
  ) => void;
  isPasting?: boolean;
  constraints?: AccountConstraint[];
}

const detail = (row: BanHangSearchParams) => constraintValues(row);

export const DetailTab: React.FC<DetailTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  onRepeatForRows,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  isPasting,
  constraints = []
}) => {
  const [contextMenu, setContextMenu] = useState<{
    position: { x: number; y: number } | null;
    rowData: BanHangRecord | null;
    columnField: string | null;
  }>({
    position: null,
    rowData: null,
    columnField: null
  });
  const { fetchDetail } = useCRUD({
    endpoint: QUERY_KEYS.VAT_TU,
    initialFetch: false
  });
  const { cellError } = useInputTableValidation({
    rows,
    name: 'chi_tiet',
    map: detail,
    deps: constraintKey(constraints)
  });
  const columns = useMemo(
    () => getDetailTableColumns(onCellValueChange, fetchDetail, cellError),
    [cellError, fetchDetail, onCellValueChange]
  );

  useDetailPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  const contextMenuOptions = useMemo(
    () =>
      ContextMenuBuilder.buildOptions({
        contextMenu,
        columns,
        getRowId: row => row?.uuid || '',
        onCopyData: ({ columnValue, columnField, columnValues }) => {
          copyCellValue({
            columnField,
            columnValue,
            columnValues,
            fallback: text => {
              console.log('Copy data per column:', text);
            }
          });
        },
        onRepeatForRows,
        rowsCount: rows.length
      }),
    [columns, contextMenu, onRepeatForRows, rows.length]
  );

  return (
    <>
      <ErpInputTableV2
        rows={rows}
        showIndexColumn
        autoHeight
        bottomSelector='[data-arito-bottom-bar]'
        onRowClick={onRowClick}
        onCellClick={onCellClick}
        onCellContextMenu={(event, params) => {
          onCellClick?.(params);
          const pointerEvent = event as MouseEvent;

          setContextMenu({
            position: { x: pointerEvent.clientX, y: pointerEvent.clientY },
            rowData: params.row,
            columnField: params.field
          });
        }}
        selectedRowId={selectedRowUuid || undefined}
        columns={columns as BanHangRecord[]}
        getRowId={row => row?.uuid || ''}
        pasteTarget={selectedCell}
        actionLoading={isPasting}
        actionsDisabled={formMode === 'view'}
        tableClassName='[&_tbody_td]:px-2 [&_tbody_td]:py-0.5 [&_tbody_td>div]:py-0 [&_thead_th]:px-2'
        actionOrder={[
          'add',
          'delete',
          'copy',
          'paste',
          'moveUp',
          'moveDown',
          'export',
          'pin',
          'viewInventory',
          'viewReceipt'
        ]}
        actions={{
          add: onAddRow,
          delete: onDeleteRow,
          copy: onCopyRow,
          paste: onPasteRow,
          moveUp: () => onMoveRow('up'),
          moveDown: () => onMoveRow('down'),
          export: () => console.log('Export clicked'),
          pin: () => console.log('Pin clicked'),
          viewInventory: () => console.log('View inventory clicked'),
          viewReceipt: () => console.log('View receipt clicked')
        }}
      />

      <ContextMenu
        options={contextMenuOptions}
        position={contextMenu.position}
        onClose={() =>
          setContextMenu({
            position: null,
            rowData: null,
            columnField: null
          })
        }
      />
    </>
  );
};
